'use client';

import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import {
  getUserPreferences,
  saveUserPreferences,
  getPublicBrandingSettings,
  DEFAULT_HEADER_MARKET_INDICATORS,
  type TickerTapeSettings,
  type HeaderMarketIndicatorsSettings,
} from '@/lib/api-client';
import { useAuth } from './AuthContext';

export type Theme = 'dark' | 'light' | 'gray' | 'blue' | 'contrast';

const VALID_THEMES: Theme[] = ['dark', 'light', 'gray', 'blue', 'contrast'];

export const DEFAULT_TICKER_TAPE_SETTINGS: TickerTapeSettings = {
  tickers: ['AAPL', 'MSFT', 'AMZN', 'GOOGL', 'TSLA', 'VTI', 'SPY', 'NFLX', 'COST'],
  sources: ['topMovers'],
  mode: 'topMovers',   // retained one release for back-compat (plan #227)
  scrollBehavior: 'alwaysPlay',
  visibility: 'marketHours',
  speed: 'normal',
  freezeOnScroll: false,
};

/** Migration helper (plan #227). Maps the legacy single-source `mode` field
 *  onto the new `sources` array when the persisted prefs were written by
 *  an older client. Pure — does not mutate the input. */
export function migrateTickerTapeSettings(s: TickerTapeSettings): TickerTapeSettings {
  if (s.sources && s.sources.length > 0) return s;
  // Empty/missing sources: derive from `mode` if present, otherwise the
  // safe default (topMovers).
  const sources: TickerTapeSettings['sources'] = s.mode === 'custom' ? ['custom'] : ['topMovers'];
  return { ...s, sources };
}

export type TickerVisibility = 'marketHours' | 'always' | 'hidden';

interface PreferencesContextValue {
  theme: Theme;
  setTheme: (t: Theme) => void;
  showAds: boolean;
  setShowAds: (v: boolean) => void;
  largeText: boolean;
  setLargeText: (v: boolean) => void;
  squareTiles: boolean;
  setSquareTiles: (v: boolean) => void;
  /** Show the daily Pre-Trading Checklist popup at the market open. Opt-in,
   *  OFF by default. Read app-wide by PreTradingChecklistModal. */
  showPreTradingChecklist: boolean;
  setShowPreTradingChecklist: (v: boolean) => void;
  tickerVisibility: TickerVisibility;
  setTickerVisibility: (v: TickerVisibility) => void;
  tickerTapeSettings: TickerTapeSettings;
  setTickerTapeSettings: (s: TickerTapeSettings) => void;
  headerMarketIndicators: HeaderMarketIndicatorsSettings;
  setHeaderMarketIndicators: (s: HeaderMarketIndicatorsSettings) => void;
}

const HEADER_MARKET_INDICATORS_KEY = 'headerMarketIndicators';

/** Coerce a value of unknown shape into a valid HeaderMarketIndicatorsSettings.
 *  Used on localStorage read and DB read — both can yield garbage if a
 *  malformed payload was ever persisted (or the schema evolved). Falls back
 *  to the default on anything it can't interpret. */
export function normalizeHeaderMarketIndicators(raw: unknown): HeaderMarketIndicatorsSettings {
  if (!raw || typeof raw !== 'object') return { ...DEFAULT_HEADER_MARKET_INDICATORS };
  const o = raw as Record<string, unknown>;
  const enabled = typeof o.enabled === 'boolean' ? o.enabled : DEFAULT_HEADER_MARKET_INDICATORS.enabled;
  const includeOil = typeof o.includeOil === 'boolean' ? o.includeOil : DEFAULT_HEADER_MARKET_INDICATORS.includeOil;
  const tickerRe = /^[A-Z][A-Z0-9.-]{0,9}$/;
  const tickers: string[] = [];
  if (Array.isArray(o.tickers)) {
    const seen = new Set<string>();
    for (const t of o.tickers) {
      if (typeof t !== 'string') continue;
      const up = t.trim().toUpperCase();
      if (!tickerRe.test(up) || seen.has(up)) continue;
      seen.add(up);
      tickers.push(up);
      if (tickers.length >= 5) break;
    }
  }
  return { enabled, tickers, includeOil };
}

const PreferencesContext = createContext<PreferencesContextValue | null>(null);

export function PreferencesProvider({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const [theme, setThemeState] = useState<Theme>('dark');
  const [showAds, setShowAdsState] = useState(false);
  const [largeText, setLargeTextState] = useState(false);
  const [squareTiles, setSquareTilesState] = useState(false);
  // Opt-in (default OFF) — only an explicit enable ('true' in localStorage /
  // true from the DB) turns the market-open checklist on.
  const [showPreTradingChecklist, setShowPCState] = useState(false);
  const [tickerTapeSettings, setTTSState] = useState<TickerTapeSettings>(DEFAULT_TICKER_TAPE_SETTINGS);
  const [headerMarketIndicators, setHMIState] = useState<HeaderMarketIndicatorsSettings>(DEFAULT_HEADER_MARKET_INDICATORS);
  const [mounted, setMounted] = useState(false);
  const [dbLoaded, setDbLoaded] = useState(false);

  // On mount: load from localStorage first (instant, no flicker)
  useEffect(() => {
    const savedTheme = localStorage.getItem('theme') as Theme | null;
    const savedAds = localStorage.getItem('showAds');
    const savedLargeText = localStorage.getItem('largeText');
    const savedSquareTiles = localStorage.getItem('squareTiles');
    if (savedTheme && VALID_THEMES.includes(savedTheme)) setThemeState(savedTheme);
    if (savedAds !== null) setShowAdsState(savedAds === 'true');
    if (savedLargeText === 'true') setLargeTextState(true);
    if (savedSquareTiles === 'true') setSquareTilesState(true);
    // Opt-in: only an explicit 'true' enables; absent / 'false' stays off.
    const savedPC = localStorage.getItem('showPreTradingChecklist');
    if (savedPC !== null) setShowPCState(savedPC === 'true');
    try {
      const savedTTS = localStorage.getItem('tickerTapeSettings');
      const parsed = savedTTS ? JSON.parse(savedTTS) as TickerTapeSettings : null;
      // Back-compat: the pre-tri-state 'showTicker' boolean maps onto the new
      // visibility enum (true → marketHours default, false → hidden).
      const legacyShowTicker = localStorage.getItem('showTicker');
      const visibility: TickerVisibility = parsed?.visibility
        ?? (legacyShowTicker === 'false' ? 'hidden' : 'marketHours');
      setTTSState(migrateTickerTapeSettings({ ...DEFAULT_TICKER_TAPE_SETTINGS, ...(parsed ?? {}), visibility }));
    } catch {}
    try {
      const savedHMI = localStorage.getItem(HEADER_MARKET_INDICATORS_KEY);
      if (savedHMI) setHMIState(normalizeHeaderMarketIndicators(JSON.parse(savedHMI)));
    } catch {}
    setMounted(true);
  }, []);

  // For logged-in users: load preferences from DB (overrides localStorage)
  useEffect(() => {
    if (!user || dbLoaded) return;
    getUserPreferences()
      .then((prefs) => {
        if (prefs.theme && VALID_THEMES.includes(prefs.theme as Theme)) {
          setThemeState(prefs.theme as Theme);
          localStorage.setItem('theme', prefs.theme);
        }
        if (prefs.largeText !== undefined) {
          setLargeTextState(prefs.largeText);
          localStorage.setItem('largeText', String(prefs.largeText));
        }
        if (prefs.squareTiles !== undefined) {
          setSquareTilesState(prefs.squareTiles);
          localStorage.setItem('squareTiles', String(prefs.squareTiles));
        }
        if (prefs.showPreTradingChecklist !== undefined) {
          setShowPCState(prefs.showPreTradingChecklist);
          localStorage.setItem('showPreTradingChecklist', String(prefs.showPreTradingChecklist));
        }
        if (prefs.tickerTapeSettings) {
          const tts: TickerTapeSettings = migrateTickerTapeSettings({
            ...DEFAULT_TICKER_TAPE_SETTINGS,
            ...prefs.tickerTapeSettings,
            visibility: prefs.tickerTapeSettings.visibility ?? 'marketHours',
          });
          setTTSState(tts);
          localStorage.setItem('tickerTapeSettings', JSON.stringify(tts));
        }
        if (prefs.headerMarketIndicators) {
          const hmi = normalizeHeaderMarketIndicators(prefs.headerMarketIndicators);
          setHMIState(hmi);
          localStorage.setItem(HEADER_MARKET_INDICATORS_KEY, JSON.stringify(hmi));
        }
        setDbLoaded(true);
      })
      .catch(() => setDbLoaded(true));
  }, [user, dbLoaded]);

  // For anonymous users: load default theme from admin branding settings
  useEffect(() => {
    if (user) return; // Only for unauthenticated
    const savedTheme = localStorage.getItem('theme');
    if (savedTheme) return; // User already picked a theme manually
    getPublicBrandingSettings()
      .then((b) => {
        const dt = (b as Record<string, unknown>).defaultTheme as string | undefined;
        if (dt && VALID_THEMES.includes(dt as Theme)) {
          setThemeState(dt as Theme);
        }
      })
      .catch(() => {});
  }, [user]);

  // Apply theme class to <html>
  useEffect(() => {
    if (!mounted) return;
    const root = document.documentElement;
    root.classList.remove('dark', 'light', 'gray', 'blue', 'contrast');
    root.classList.add(theme);
  }, [theme, mounted]);

  // Apply large-text class to <html>
  useEffect(() => {
    if (!mounted) return;
    const root = document.documentElement;
    if (largeText) {
      root.classList.add('large-text');
    } else {
      root.classList.remove('large-text');
    }
  }, [largeText, mounted]);

  // Apply square-tiles class to <html>. CSS overrides the `rounded-xl`
  // bevel on dashboard tile chrome to 0 when this class is present.
  useEffect(() => {
    if (!mounted) return;
    const root = document.documentElement;
    if (squareTiles) {
      root.classList.add('square-tiles');
    } else {
      root.classList.remove('square-tiles');
    }
  }, [squareTiles, mounted]);

  const setTheme = useCallback((t: Theme) => {
    setThemeState(t);
    localStorage.setItem('theme', t);
    if (user) saveUserPreferences({ theme: t }).catch(() => {});
  }, [user]);

  const setShowAds = useCallback((v: boolean) => {
    setShowAdsState(v);
    localStorage.setItem('showAds', String(v));
  }, []);

  const setLargeText = useCallback((v: boolean) => {
    setLargeTextState(v);
    localStorage.setItem('largeText', String(v));
    if (user) saveUserPreferences({ largeText: v }).catch(() => {});
  }, [user]);

  const setSquareTiles = useCallback((v: boolean) => {
    setSquareTilesState(v);
    localStorage.setItem('squareTiles', String(v));
    if (user) saveUserPreferences({ squareTiles: v }).catch(() => {});
  }, [user]);

  const setShowPreTradingChecklist = useCallback((v: boolean) => {
    setShowPCState(v);
    localStorage.setItem('showPreTradingChecklist', String(v));
    if (user) saveUserPreferences({ showPreTradingChecklist: v }).catch(() => {});
  }, [user]);

  const setTickerTapeSettings = useCallback((s: TickerTapeSettings) => {
    setTTSState(s);
    localStorage.setItem('tickerTapeSettings', JSON.stringify(s));
    if (user) saveUserPreferences({ tickerTapeSettings: s }).catch(() => {});
  }, [user]);

  const tickerVisibility: TickerVisibility = tickerTapeSettings.visibility ?? 'marketHours';
  const setTickerVisibility = useCallback((v: TickerVisibility) => {
    const next = { ...tickerTapeSettings, visibility: v };
    setTickerTapeSettings(next);
  }, [tickerTapeSettings, setTickerTapeSettings]);

  const setHeaderMarketIndicators = useCallback((s: HeaderMarketIndicatorsSettings) => {
    const normalized = normalizeHeaderMarketIndicators(s);
    setHMIState(normalized);
    localStorage.setItem(HEADER_MARKET_INDICATORS_KEY, JSON.stringify(normalized));
    if (user) saveUserPreferences({ headerMarketIndicators: normalized }).catch(() => {});
  }, [user]);

  return (
    <PreferencesContext.Provider value={{ theme, setTheme, showAds, setShowAds, largeText, setLargeText, squareTiles, setSquareTiles, showPreTradingChecklist, setShowPreTradingChecklist, tickerVisibility, setTickerVisibility, tickerTapeSettings, setTickerTapeSettings, headerMarketIndicators, setHeaderMarketIndicators }}>
      {children}
    </PreferencesContext.Provider>
  );
}

export function usePreferences(): PreferencesContextValue {
  const ctx = useContext(PreferencesContext);
  if (!ctx) throw new Error('usePreferences must be used within PreferencesProvider');
  return ctx;
}
